import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { bmsApi } from '@/app/bmsApi';
import { actsAndDocumentsSlice } from '@/features/actsAndDocuments/actsAndDocumentsSlice';
import { appSettingsApi } from '@/features/appSettings/appSettingsApi';
import { auditApi } from '@/features/audit/auditApi';
import authSlice from '@/features/auth/authSlice';
import { commercialMeteringPointsApi } from '@/features/commercialMeteringPoint/commercialMeteringPointsApi';
import { departmentsApi } from '@/features/departments/departmentsApi';
import { employeePositionApi } from '@/features/employeePositions/employeePositionsApi';
import { employeesApi } from '@/features/employees/employeesApi';
import { equipmentGroupApi } from '@/features/equipmentGroup';
import { equipmentManufacturerApi } from '@/features/equipmentManufacturerApi';
import { equipmentTypeApi } from '@/features/equipmentTypeApi';
import { gasConsumingEquipmentApi } from '@/features/gasConsumingEquipmentApi';
import { gasConsumingEquipmentCommercialApi } from '@/features/gasConsumingEquipmentCommercialApi';
import { gasPipelineSectionsApi } from '@/features/gasPipelineSectionsApi';
import { addressesApi } from '@/features/addresses/addressesApi';
import { locationsApi } from '@/features/locations/locationsApi';
import { meteringDevicesApi } from '@/features/meteringDevicesApi';
import { meteringDevicesCommercialApi } from '@/features/meteringDevicesCommercial';
import { meteringNodeApi } from '@/features/meteringNode/meteringNodeApi';
import { meteringNodeCommercialApi } from '@/features/meteringNodeCommercial';
import { meteringNodeKindApi } from '@/features/meteringNodeKind/meteringNodeKindApi';
import { meteringNodeLocationTypeApi } from '@/features/meteringNodeLocationType/meteringNodeLocationTypeApi';
import { meteringPointApi } from '@/features/meteringPoint/meteringPointApi';
import { meteringSealApi } from '@/features/meteringSealApi';
import { nodeSealLocationApi } from '@/features/nodeSealLocation/nodeSealLocationApi';
import { nodeSealTypesApi } from '@/features/nodeSealTypes/nodeSealTypesApi';
import { organizationsApi } from '@/features/organizations/organizationsApi';
import { rolesApi } from '@/features/roles/rolesApi';
import { seasonTypesApi } from '@/features/seasonTypes/seasonTypesApi';
import { serviceOrganizationsApi } from '@/features/serviceOrganizationsApi';
import { sessionsApi } from '@/features/sessions/sessionsApi';
import { subdivisionsApi } from '@/features/subdivisions/subdivisionsApi';
import uiSlice from '@/features/ui/uiSlice';

const rootReducer = combineReducers({
  [bmsApi.reducerPath]: bmsApi.reducer,
  [uiSlice.name]: uiSlice.reducer,
  [appSettingsApi.reducerPath]: appSettingsApi.reducer,
  [authSlice.name]: authSlice.reducer,
  [organizationsApi.reducerPath]: organizationsApi.reducer,
  [subdivisionsApi.reducerPath]: subdivisionsApi.reducer,
  [employeesApi.reducerPath]: employeesApi.reducer,
  [sessionsApi.reducerPath]: sessionsApi.reducer,
  [meteringNodeApi.reducerPath]: meteringNodeApi.reducer,
  [meteringNodeLocationTypeApi.reducerPath]:
    meteringNodeLocationTypeApi.reducer,
  [seasonTypesApi.reducerPath]: seasonTypesApi.reducer,
  [meteringNodeKindApi.reducerPath]: meteringNodeKindApi.reducer,
  [commercialMeteringPointsApi.reducerPath]:
    commercialMeteringPointsApi.reducer,
  [meteringPointApi.reducerPath]: meteringPointApi.reducer,
  [addressesApi.reducerPath]: addressesApi.reducer,
  [locationsApi.reducerPath]: locationsApi.reducer,
  [meteringSealApi.reducerPath]: meteringSealApi.reducer,
  [nodeSealTypesApi.reducerPath]: nodeSealTypesApi.reducer,
  [nodeSealLocationApi.reducerPath]: nodeSealLocationApi.reducer,
  [meteringDevicesApi.reducerPath]: meteringDevicesApi.reducer,
  [rolesApi.reducerPath]: rolesApi.reducer,
  [gasConsumingEquipmentApi.reducerPath]: gasConsumingEquipmentApi.reducer,
  [equipmentGroupApi.reducerPath]: equipmentGroupApi.reducer,
  [equipmentManufacturerApi.reducerPath]: equipmentManufacturerApi.reducer,
  [equipmentTypeApi.reducerPath]: equipmentTypeApi.reducer,
  [serviceOrganizationsApi.reducerPath]: serviceOrganizationsApi.reducer,
  [gasPipelineSectionsApi.reducerPath]: gasPipelineSectionsApi.reducer,
  [actsAndDocumentsSlice.name]: actsAndDocumentsSlice.reducer,
  [meteringDevicesCommercialApi.reducerPath]:
    meteringDevicesCommercialApi.reducer,
  [gasConsumingEquipmentCommercialApi.reducerPath]:
    gasConsumingEquipmentCommercialApi.reducer,
  [meteringNodeCommercialApi.reducerPath]: meteringNodeCommercialApi.reducer,
  [auditApi.reducerPath]: auditApi.reducer,
  [employeePositionApi.reducerPath]: employeePositionApi.reducer,
  [departmentsApi.reducerPath]: departmentsApi.reducer,
});

const middlewares = [
  bmsApi.middleware,
  appSettingsApi.middleware,
  organizationsApi.middleware,
  subdivisionsApi.middleware,
  meteringPointApi.middleware,
  addressesApi.middleware,
  locationsApi.middleware,
  employeesApi.middleware,
  sessionsApi.middleware,
  meteringNodeApi.middleware,
  seasonTypesApi.middleware,
  meteringNodeKindApi.middleware,
  meteringNodeLocationTypeApi.middleware,
  rolesApi.middleware,
  meteringSealApi.middleware,
  nodeSealTypesApi.middleware,
  nodeSealLocationApi.middleware,
  meteringDevicesApi.middleware,
  commercialMeteringPointsApi.middleware,
  serviceOrganizationsApi.middleware,
  gasConsumingEquipmentApi.middleware,
  equipmentGroupApi.middleware,
  equipmentManufacturerApi.middleware,
  equipmentTypeApi.middleware,
  gasPipelineSectionsApi.middleware,
  meteringDevicesCommercialApi.middleware,
  gasConsumingEquipmentCommercialApi.middleware,
  meteringNodeCommercialApi.middleware,
  auditApi.middleware,
  employeePositionApi.middleware,
  departmentsApi.middleware,
];

const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(middlewares),
});

export default store;

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
